import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import {
  Plus, FolderOpen, Receipt, Search, X, ChevronDown, Calendar, DollarSign, FileText, User, Loader2, Download, Trash2, Edit, Filter, CheckCircle, Eye
} from 'lucide-react';
import FilterDropdown from '../components/FilterDropdown';

// ─── Styles ──────────────────────────────────────────────
const styles = {
  customScrollbar: `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #0A5F38;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #084d2e;
    }
  `,
  page: { fontFamily: "'Inter','Segoe UI',sans-serif", minHeight: '100vh', paddingBottom: 40 },
  header: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 800, color: '#0A5F38', margin: '0 0 4px 0' },
  subtitle: { fontSize: 14, color: '#64748b', margin: 0 },
  tabsContainer: {
    display: 'flex', gap: 12, marginBottom: 12, padding: '0 8px'
  },
  tab: (active) => ({
    padding: '10px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 14, transition: 'all .2s',
    background: 'transparent', border: 'none', borderBottom: active ? '3px solid #0A5F38' : '3px solid transparent',
    color: active ? '#0A5F38' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 6
  }),
  card: {
    background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
    boxShadow: '0 4px 20px rgba(0,0,0,.04)', overflow: 'hidden'
  },
  cardHeader: {
    padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12
  },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#334155', margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '14px 20px', textAlign: 'center', fontSize: 11, fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px',
    background: '#f8fafc', borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '14px 20px', fontSize: 14, color: '#334155', borderBottom: '1px solid #f1f5f9',
    textAlign: 'center'
  },
  btn: (variant = 'primary') => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px',
    borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
    transition: 'all .2s',
    background: variant === 'primary' ? '#0A5F38' : variant === 'danger' ? '#ef4444' : variant === 'outline' ? '#fff' : '#f1f5f9',
    color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#fff' : variant === 'outline' ? '#0A5F38' : '#334155',
    border: variant === 'outline' ? '2px solid #0A5F38' : 'none'
  }),
  actionBtn: (color) => ({
    padding: 6, borderRadius: 6, border: 'none', cursor: 'pointer',
    background: 'transparent', color: color, transition: 'background .2s',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }),
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)'
  },
  modal: {
    background: '#fff', borderRadius: 24, width: '100%', maxWidth: 560,
    boxShadow: '0 25px 50px -12px rgba(0,0,0,.25)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column'
  },
  modalBody: {
    padding: 30, overflowY: 'auto', maxHeight: 'calc(90vh - 160px)'
  },
  filterBar: {
    background: '#fff', padding: '16px 24px', borderRadius: 12, border: '1px solid #e2e8f0',
    display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20, alignItems: 'flex-end'
  },
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24
  },
  statCard: (bg) => ({
    background: bg || '#fff', padding: '20px 24px', borderRadius: 16,
    boxShadow: '0 4px 15px rgba(0,0,0,.05)', border: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', gap: 4
  }),
  statValue: (color) => ({ fontSize: 26, fontWeight: 800, color: color || '#1e293b' }),
  statLabel: { fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px' },
  badge: (type) => {
    const colors = {
      invoice: { bg: '#dbeafe', text: '#1e40af' },
      closed_order: { bg: '#dcfce7', text: '#166534' },
      admin: { bg: '#fef3c7', text: '#92400e' },
      employee: { bg: '#f3f4f6', text: '#374151' },
      pending: { bg: '#fef3c7', text: '#d97706' },
      approved: { bg: '#dcfce7', text: '#16a34a' },
      rejected: { bg: '#fee2e2', text: '#dc2626' }
    };
    const c = colors[type] || colors.employee;
    return {
      display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11,
      fontWeight: 700, background: c.bg, color: c.text, textTransform: 'uppercase',
      border: `1px solid ${c.text}20`
    };
  }
};

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const AdminExpenses = () => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [projects, setProjects] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [summary, setSummary] = useState({ totalAmount: 0, count: 0 });

  // Filters
  const [filterProject, setFilterProject] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterDocNum, setFilterDocNum] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [expenseSearch, setExpenseSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination & Selection
  const [projectsPage, setProjectsPage] = useState(1);
  const [projectsTotalPages, setProjectsTotalPages] = useState(1);
  const [expensesPage, setExpensesPage] = useState(1);
  const [expensesTotalPages, setExpensesTotalPages] = useState(1);
  const [selectedExpenses, setSelectedExpenses] = useState([]);

  // Modals
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedExpenseForView, setSelectedExpenseForView] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({ title: '', message: '', onConfirm: () => {}, danger: false });
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({});
  const [docSearch, setDocSearch] = useState('');
  const [showDocDropdown, setShowDocDropdown] = useState(false);

  const openViewModal = (e) => {
    setSelectedExpenseForView(e);
    setShowViewModal(true);
  };

  const hasApprovedSelection = useMemo(() => {
    return selectedExpenses.some(id => {
      const exp = expenses.find(e => e._id === id);
      return exp && exp.status === 'approved';
    });
  }, [selectedExpenses, expenses]);

  // ─── Data Fetching ───────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const expensesParams = {
        projectId: selectedProject ? selectedProject._id : filterProject,
        employeeName: filterEmployee,
        linkedDocumentNumber: filterDocNum,
        fromDate: filterFrom,
        toDate: filterTo,
        page: expensesPage,
        limit: 10,
        search: expenseSearch,
        sortBy,
        sortOrder
      };

      const [pRes, eRes, empRes, docRes] = await Promise.all([
        axios.get(`/expenses/projects?page=${projectsPage}&limit=10&search=${encodeURIComponent(projectSearch)}`),
        axios.get('/expenses', { params: expensesParams }),
        axios.get('/admin/salespersons'),
        axios.get('/expenses/linked-documents')
      ]);

      setProjects(pRes.data.projects || []);
      setProjectsTotalPages(pRes.data.totalPages || 1);
      setExpenses(eRes.data.expenses || []);
      setExpensesTotalPages(eRes.data.totalPages || 1);
      setSummary(eRes.data.summary || { totalAmount: 0, count: 0 });

      // Include current admin in the employees list for self-expense attribution
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      let empList = empRes.data || [];
      if (currentUser.name && !empList.find(e => e.name === currentUser.name)) {
        empList = [{ id: currentUser.id || currentUser._id, name: currentUser.name, role: 'admin' }, ...empList];
      }
      setEmployees(empList);
      setLinkedDocs(docRes.data.documents || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterProject, filterEmployee, filterDocNum, filterFrom, filterTo, projectsPage, expensesPage, selectedProject, projectSearch, expenseSearch, sortBy, sortOrder]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Reset selection on page or filter change
  useEffect(() => { setSelectedExpenses([]); }, [expensesPage, filterProject, filterEmployee, filterDocNum, filterFrom, filterTo, selectedProject, expenseSearch]);
  useEffect(() => { setProjectsPage(1); setExpensesPage(1); }, [filterProject, filterEmployee, filterDocNum, filterFrom, filterTo, projectSearch, selectedProject, expenseSearch]);

  const handleSort = (key) => {
    let order = 'asc';
    if (sortBy === key && sortOrder === 'asc') {
      order = 'desc';
    }
    setSortBy(key);
    setSortOrder(order);
  };

  const handleDocumentClick = (e, type, number) => {
    e.stopPropagation();
    if (!type || !number) return;
    
    // Convert docType to internal type formats (Expense DB might use 'invoice', 'closed_order', 'estimate')
    const typeStr = type.toLowerCase();
    
    if (typeStr.includes('invoice') || typeStr === 'inv') {
      navigate(`/admin/invoices?view=${number}`);
    } else if (typeStr.includes('closed_order') || typeStr === 'closed_order' || typeStr === 'co') {
      navigate(`/admin/closed-orders?view=${number}`);
    } else if (typeStr.includes('estimate') || typeStr === 'est') {
      navigate(`/admin/estimates?view=${number}`);
    } else if (type === 'Invoice') {
      toast('Document type Invoice view not mapped yet', { icon: 'ℹ️' });
    } else {
      toast('Document type view not mapped yet', { icon: 'ℹ️' });
    }
  };

  // ─── CRUD Handlers ───────────────────────────────────
  const handleDeleteProject = async (id) => {
    setConfirmConfig({
      title: 'Delete Project?',
      message: 'Are you sure you want to delete this project? This will also delete all associated expenses.',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/expenses/projects/${id}?force=true`);
          toast.success('Project and associated expenses deleted');
          fetchAll();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to delete project');
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteExpense = async (id) => {
    setConfirmConfig({
      title: 'Delete Expense?',
      message: 'Are you sure you want to delete this expense entry?',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/expenses/${id}`);
          toast.success('Expense deleted');
          fetchAll();
        } catch (err) {
          toast.error('Failed to delete expense');
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleApproveExpense = async (id) => {
    try {
      setLoading(true);
      await axios.put(`/expenses/${id}/approve`);
      toast.success('Expense approved');
      fetchAll();
    } catch (err) {
      toast.error('Failed to approve expense');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    setConfirmConfig({
      title: 'Bulk Approve',
      message: `Approve ${selectedExpenses.length} selected expenses?`,
      danger: false,
      onConfirm: async () => {
        try {
          setLoading(true);
          const res = await axios.put('/expenses/bulk-approve', { expenseIds: selectedExpenses });
          toast.success(res.data.message);
          setSelectedExpenses([]);
          fetchAll();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to bulk approve');
        } finally { setLoading(false); }
      }
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = async () => {
    setConfirmConfig({
      title: 'Bulk Delete',
      message: `Delete ${selectedExpenses.length} selected expenses? This action cannot be undone.`,
      danger: true,
      onConfirm: async () => {
        try {
          setLoading(true);
          const res = await axios.post('/expenses/bulk-delete', { expenseIds: selectedExpenses });
          toast.success(res.data.message);
          setSelectedExpenses([]);
          fetchAll();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to bulk delete');
        } finally { setLoading(false); }
      }
    });
    setShowConfirmModal(true);
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();
    if (!formData.projectName?.trim()) return toast.error('Project Name is required');
    try {
      setSaving(true);
      if (editingItem) {
        await axios.put(`/expenses/projects/${editingItem._id}`, formData);
        toast.success('Project updated');
      } else {
        await axios.post('/expenses/projects', formData);
        toast.success('Project created');
      }
      setShowProjectModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save project');
    } finally { setSaving(false); }
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    if (!formData.projectId) return toast.error('Project is required');
    if (!formData.employeeName) return toast.error('Employee is required');

    try {
      setSaving(true);
      if (editingItem) {
        // Single update
        const payload = {
          ...formData,
          amount: parseFloat(formData.amount),
          date: formData.date || new Date().toISOString().slice(0, 10)
        };
        await axios.put(`/expenses/${editingItem._id}`, payload);
        toast.success('Expense updated');
      } else {
        // Batch create
        const validItems = (formData.items || []).filter(it => it.amount && parseFloat(it.amount) > 0 && it.expenseDescription?.trim());
        if (validItems.length === 0) return toast.error('Please add at least one valid expense item');

        const payload = {
          projectId: formData.projectId,
          date: formData.date || new Date().toISOString().slice(0, 10),
          employeeName: formData.employeeName,
          employeeId: formData.employeeId,
          employeeRole: formData.employeeRole,
          items: validItems
        };
        await axios.post('/expenses', payload);
        toast.success(`${validItems.length} Expenses added`);
      }
      setShowExpenseModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save expense');
    } finally { setSaving(false); }
  };

  // ─── Export ──────────────────────────────────────────
  const exportCSV = () => {
    if (expenses.length === 0) return toast.error('No data to export');
    const headers = ['Date', 'Project', 'Employee', 'Description', 'Amount', 'Notes', 'Linked Doc'];
    const rows = expenses.map(e => [
      fmtDate(e.date),
      e.projectName,
      e.employeeName,
      e.expenseDescription,
      e.amount,
      e.notes || '',
      e.projectId?.linkedDocumentNumber || ''
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Expenses_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast.success('Report exported!');
  };

  // ─── Render Helpers ──────────────────────────────────
  const openProjectModal = (p = null) => {
    setEditingItem(p);
    setFormData(p ? { ...p, linkedDocuments: p.linkedDocuments || [] } : { projectName: '', linkedDocuments: [], linkedDocumentId: null, linkedDocumentNumber: '', linkedDocumentType: null });
    setDocSearch('');
    setShowProjectModal(true);
  };

  const openExpenseModal = (e = null) => {
    setEditingItem(e);
    const formatDate = (d) => {
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? new Date().toISOString().slice(0, 10) : dt.toISOString().slice(0, 10);
    };

    if (e) {
      setFormData({
        ...e,
        projectId: e.projectId?._id || e.projectId,
        date: formatDate(e.date)
      });
    } else {
      setFormData({
        projectId: selectedProject ? selectedProject._id : '',
        date: formatDate(new Date()),
        employeeName: '',
        employeeId: '',
        employeeRole: 'employee',
        items: [
          { amount: '', expenseDescription: '', notes: '' },
          { amount: '', expenseDescription: '', notes: '' },
          { amount: '', expenseDescription: '', notes: '' }
        ]
      });
    }
    setShowExpenseModal(true);
  };

  const filteredDocs = linkedDocs.filter(d => d.number?.toLowerCase().includes(docSearch.toLowerCase()) || d.customerName?.toLowerCase().includes(docSearch.toLowerCase()));

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedExpenses(expenses.map(exp => exp._id));
    } else {
      setSelectedExpenses([]);
    }
  };

  const toggleSelectExpense = (id) => {
    setSelectedExpenses(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div style={styles.page}>
      <style>{styles.customScrollbar}</style>
      <div style={styles.header}>
        <h1 style={styles.title}>Project Expense Management</h1>
        <p style={styles.subtitle}>Track, audit, and manage organization-wide expenses</p>
      </div>

      {/* KPI Cards */}
      <div style={styles.summaryGrid}>
        <div style={styles.statCard('linear-gradient(135deg, #0A5F38 0%, #0d7a4a 100%)')}>
          <span style={{ ...styles.statLabel, color: 'rgba(255,255,255,0.7)' }}>{selectedProject ? 'Project Spent' : 'Total Expenses'}</span>
          <span style={{ ...styles.statValue('#fff') }}>{fmt(summary.totalAmount)}</span>
        </div>
        <div style={styles.statCard()}>
          <span style={styles.statLabel}>{selectedProject ? 'Approved Amount' : 'Total Projects'}</span>
          <span style={styles.statValue('#0A5F38')}>{selectedProject ? fmt(summary.approvedAmount) : projects.length}</span>
        </div>
        <div style={styles.statCard()}>
          <span style={styles.statLabel}>{selectedProject ? 'Pending Amount' : 'Expense Entries'}</span>
          <span style={styles.statValue('#C5A57D')}>{selectedProject ? fmt(summary.pendingAmount) : summary.count}</span>
        </div>
        <div style={styles.statCard()}>
          <span style={styles.statLabel}>{selectedProject ? 'Team Members' : 'Active Employees'}</span>
          <span style={styles.statValue('#1e293b')}>{selectedProject ? summary.employeeCount : employees.filter(e => e.is_active).length}</span>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={styles.filterBar}>
        {!selectedProject ? (
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Search Projects</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }}
                placeholder="Search by Project Name or Doc #..."
                value={projectSearch}
                onChange={e => setProjectSearch(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Employee</label>
              <FilterDropdown
                value={filterEmployee}
                onChange={e => setFilterEmployee(e.target.value)}
                options={[
                  { value: '', label: 'All Employees' },
                  ...employees.map(e => ({ value: e.name, label: e.name }))
                ]}
              />
            </div>

            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>From</label>
              <input type="date" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>To</label>
              <input type="date" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }} value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={styles.btn('outline')} onClick={exportCSV}><Download size={14} /> Export</button>
          {!selectedProject ? (
            <button style={styles.btn('primary')} onClick={() => openProjectModal()}>
              <Plus size={14} /> New Project
            </button>
          ) : (
            <button style={styles.btn('primary')} onClick={() => openExpenseModal()}>
              <Plus size={14} /> Add Expense
            </button>
          )}
        </div>
      </div>

      {/* Main Table Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {selectedProject && (
              <button 
                onClick={() => setSelectedProject(null)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, transition: 'background .2s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} /> Back
              </button>
            )}
            <h2 style={styles.cardTitle}>{!selectedProject ? 'Project Directory' : `Project: ${selectedProject.projectName}`}</h2>
            {loading && <Loader2 size={18} className="animate-spin" color="#0A5F38" />}
            
            {selectedProject && (
              <div style={{ position: 'relative', width: 250, marginLeft: 12 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  style={{ width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }}
                  placeholder="Search by doc, employee, amount..."
                  value={expenseSearch}
                  onChange={e => setExpenseSearch(e.target.value)}
                />
              </div>
            )}
          </div>
          {selectedProject && selectedExpenses.length > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                style={{ ...styles.btn('primary'), opacity: hasApprovedSelection ? 0.6 : 1, cursor: hasApprovedSelection ? 'not-allowed' : 'pointer' }} 
                onClick={handleBulkApprove} 
                disabled={loading || hasApprovedSelection}
                title={hasApprovedSelection ? 'Some selected items are already approved' : ''}
              >
                <CheckCircle size={14} /> Approve ({selectedExpenses.length})
              </button>
              <button style={styles.btn('danger')} onClick={handleBulkDelete} disabled={loading}>
                <Trash2 size={14} /> Delete ({selectedExpenses.length})
              </button>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              {!selectedProject ? (
                <tr>
                  <th style={styles.th}>Project Name</th>
                  <th style={styles.th}>Linked Doc</th>
                  <th style={styles.th}>Total Spent</th>
                  <th style={styles.th}>Entries</th>
                  <th style={styles.th}>Created By</th>
                  <th style={styles.th}>Created Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              ) : (
                <tr>
                  <th style={{ ...styles.th, width: 40 }}>
                    <input type="checkbox" checked={expenses.length > 0 && selectedExpenses.length === expenses.length} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                  </th>
                  <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('date')}>
                    Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={styles.th}>Linked Doc</th>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('amount')}>
                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('status')}>
                    Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={styles.th}>Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Fetching data...</td></tr>
              ) : !selectedProject ? (
                projects.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No projects found</td></tr>
                ) : projects.map(p => (
                  <tr key={p._id} style={{ transition: 'background .2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ ...styles.td, fontWeight: 700 }}>
                      <span onClick={() => setSelectedProject(p)} style={{ cursor: 'pointer', color: '#0A5F38' }}>{p.projectName}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                        {p.linkedDocuments && p.linkedDocuments.length > 0 ? p.linkedDocuments.map((doc, idx) => (
                          <span key={idx} onClick={(e) => handleDocumentClick(e, doc.docType || doc.type, doc.number)} style={{ ...styles.badge(doc.docType || doc.type), cursor: 'pointer' }} title="Click to view document">
                            {doc.number}
                          </span>
                        )) : p.linkedDocumentNumber ? (
                          <span onClick={(e) => handleDocumentClick(e, p.linkedDocumentType, p.linkedDocumentNumber)} style={{ ...styles.badge(p.linkedDocumentType), cursor: 'pointer' }} title="Click to view document">
                            {p.linkedDocumentNumber}
                          </span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </div>
                    </td>
                    <td style={{ ...styles.td, fontWeight: 700, color: '#0A5F38' }}>{fmt(p.totalExpenses)}</td>
                    <td style={styles.td}>{p.expenseCount}</td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{p.createdByName}</span>
                    </td>
                    <td style={styles.td}>{fmtDate(p.createdAt)}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button style={{ ...styles.btn('outline'), padding: '4px 8px', fontSize: 11, borderRadius: 4, borderWidth: 1 }} onClick={() => setSelectedProject(p)}>View Expenses</button>
                        <button style={styles.actionBtn('#0A5F38')} onClick={() => openProjectModal(p)}><Edit size={16} /></button>
                        <button style={styles.actionBtn('#ef4444')} onClick={() => handleDeleteProject(p._id)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                expenses.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No expense entries found</td></tr>
                ) : expenses.map(e => (
                  <tr key={e._id} style={{ transition: 'background .2s', background: selectedExpenses.includes(e._id) ? '#f0fdf4' : '' }} onMouseEnter={ev => ev.currentTarget.style.background = selectedExpenses.includes(e._id) ? '#dcfce7' : '#f8fafc'} onMouseLeave={ev => ev.currentTarget.style.background = selectedExpenses.includes(e._id) ? '#f0fdf4' : ''}>
                    <td style={styles.td}>
                      <input type="checkbox" checked={selectedExpenses.includes(e._id)} onChange={() => toggleSelectExpense(e._id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={styles.td}>{fmtDate(e.date)}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                        {e.linkedDocumentNumber ? (
                          <span onClick={(ev) => handleDocumentClick(ev, e.linkedDocumentType, e.linkedDocumentNumber)} style={{ ...styles.badge(e.linkedDocumentType), cursor: 'pointer' }} title="Click to view document">
                            {e.linkedDocumentNumber}
                          </span>
                        ) : e.projectId?.linkedDocuments && e.projectId.linkedDocuments.length > 0 ? e.projectId.linkedDocuments.map((doc, idx) => (
                          <span key={idx} onClick={(ev) => handleDocumentClick(ev, doc.docType || doc.type, doc.number)} style={{ ...styles.badge(doc.docType || doc.type), cursor: 'pointer' }} title="Click to view document">
                            {doc.number}
                          </span>
                        )) : e.projectId?.linkedDocumentNumber ? (
                          <span onClick={(ev) => handleDocumentClick(ev, e.projectId.linkedDocumentType, e.projectId.linkedDocumentNumber)} style={{ ...styles.badge(e.projectId.linkedDocumentType), cursor: 'pointer' }} title="Click to view document">
                            {e.projectId.linkedDocumentNumber}
                          </span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>{e.employeeName}</span>
                        <span style={{ fontSize: 10, textTransform: 'uppercase', color: '#94a3b8' }}>{e.employeeRole}</span>
                      </div>
                    </td>
                    <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.expenseDescription}</td>
                    <td style={{ ...styles.td, fontWeight: 700, color: '#0A5F38' }}>{fmt(e.amount)}</td>
                    <td style={styles.td}><span style={styles.badge(e.status || 'pending')}>{e.status || 'pending'}</span></td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button style={styles.actionBtn('#0A5F38')} title="View" onClick={() => openViewModal(e)}><Eye size={16} /></button>
                        {e.status !== 'approved' && (
                          <button style={styles.actionBtn('#16a34a')} title="Approve" onClick={() => handleApproveExpense(e._id)}><CheckCircle size={16} /></button>
                        )}
                        <button style={styles.actionBtn('#0A5F38')} onClick={() => openExpenseModal(e)}><Edit size={16} /></button>
                        <button style={styles.actionBtn('#ef4444')} onClick={() => handleDeleteExpense(e._id)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Showing Page <b>{!selectedProject ? projectsPage : expensesPage}</b> of <b>{!selectedProject ? projectsTotalPages : expensesTotalPages}</b>
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              style={styles.btn('outline')} 
              disabled={!selectedProject ? projectsPage === 1 : expensesPage === 1}
              onClick={() => !selectedProject ? setProjectsPage(p => Math.max(1, p - 1)) : setExpensesPage(p => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button 
              style={styles.btn('outline')} 
              disabled={!selectedProject ? projectsPage >= projectsTotalPages : expensesPage >= expensesTotalPages}
              onClick={() => !selectedProject ? setProjectsPage(p => p + 1) : setExpensesPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ─── View Expense Modal ──────────────────────── */}
      {showViewModal && selectedExpenseForView && (
        <div style={styles.overlay} onClick={() => setShowViewModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', background: '#0A5F38', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ p: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                  <Receipt size={20} color="#fff" />
                </div>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Expense Details</h2>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: 0 }}>Review full transaction details</p>
                </div>
              </div>
              <button onClick={() => setShowViewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                <X size={22} color="#fff" />
              </button>
            </div>
            
            <div className="custom-scrollbar" style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Project Name</label>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{selectedExpenseForView.projectName}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transaction Date</label>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{fmtDate(selectedExpenseForView.date)}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</label>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0A5F38', marginTop: 4 }}>{fmt(selectedExpenseForView.amount)}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Approval Status</label>
                  <div style={{ marginTop: 6 }}><span style={styles.badge(selectedExpenseForView.status || 'pending')}>{selectedExpenseForView.status || 'pending'}</span></div>
                </div>
                <div style={{ gridColumn: 'span 2', background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expense Description</label>
                  <div style={{ fontSize: 14, color: '#334155', marginTop: 8, lineHeight: 1.6, fontWeight: 500 }}>{selectedExpenseForView.expenseDescription}</div>
                </div>
                {selectedExpenseForView.notes && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Internal Notes</label>
                    <div style={{ fontSize: 14, color: '#475569', marginTop: 4, fontStyle: 'italic' }}>{selectedExpenseForView.notes}</div>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Linked Document</label>
                  <div style={{ marginTop: 6 }}>
                    {selectedExpenseForView.linkedDocumentNumber ? (
                      <span 
                        style={{ ...styles.badge(selectedExpenseForView.linkedDocumentType === 'invoice' ? 'invoice' : 'closed_order'), cursor: 'pointer', padding: '4px 12px' }}
                        onClick={(ev) => handleDocumentClick(ev, selectedExpenseForView.linkedDocumentType, selectedExpenseForView.linkedDocumentNumber)}
                      >
                        {selectedExpenseForView.linkedDocumentNumber}
                      </span>
                    ) : <span style={{ color: '#cbd5e1', fontSize: 13 }}>No linked document</span>}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Attributed To</label>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginTop: 4 }}>{selectedExpenseForView.employeeName || 'Unknown'}</div>
                </div>
              </div>
            </div>
            
            <div style={{ padding: '20px 30px', background: '#fff', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setShowViewModal(false)} style={styles.btn('outline')}>Close</button>
              {selectedExpenseForView.status !== 'approved' && (
                <button onClick={() => { setShowViewModal(false); handleApproveExpense(selectedExpenseForView._id); }} style={styles.btn('primary')}>Approve</button>
              )}
              <button onClick={() => { setShowViewModal(false); openExpenseModal(selectedExpenseForView); }} style={styles.btn('outline')}>Edit</button>
              <button onClick={() => { setShowViewModal(false); handleDeleteExpense(selectedExpenseForView._id); }} style={{ ...styles.btn('outline'), color: '#ef4444', borderColor: '#ef4444' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div style={styles.overlay} onClick={() => setShowProjectModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', background: '#0A5F38', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ p: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                  <FolderOpen size={20} color="#fff" />
                </div>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{editingItem ? 'Edit Project' : 'New Project'}</h2>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: 0 }}>Organize and track project costs</p>
                </div>
              </div>
              <button onClick={() => setShowProjectModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                <X size={22} color="#fff" />
              </button>
            </div>
            <form onSubmit={handleSaveProject}>
              <div className="custom-scrollbar" style={styles.modalBody}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Project Name *</label>
                  <input
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14, outline: 'none' }}
                    placeholder="e.g., Residential Interior - Apartment 402"
                    value={formData.projectName || ''} onChange={e => setFormData({ ...formData, projectName: e.target.value })} autoFocus
                  />
                </div>
                <div style={{ marginBottom: 20, position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Link to Invoice / Closed Order (Optional)</label>
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      style={{ width: '100%', padding: '12px 16px 12px 38px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14, outline: 'none' }}
                      placeholder="Search by number or customer name..."
                      value={docSearch}
                      onChange={e => { setDocSearch(e.target.value); setShowDocDropdown(true); }}
                      onFocus={() => setShowDocDropdown(true)}
                    />
                  </div>

                  {/* Selected Documents List */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                    {(formData.linkedDocuments || []).map((doc, idx) => (
                      <div key={idx} style={{ background: '#f1f5f9', padding: '6px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, border: '1px solid #e2e8f0' }}>
                        <span style={styles.badge(doc.docType || doc.type)}>{(doc.docType || doc.type) === 'invoice' ? 'INV' : 'CO'}</span>
                        <span style={{ fontWeight: 600 }}>{doc.number}</span>
                        <button 
                          type="button" 
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                          onClick={() => {
                            const newDocs = formData.linkedDocuments.filter((_, i) => i !== idx);
                            setFormData({ ...formData, linkedDocuments: newDocs });
                          }}
                        >
                          <X size={14} color="#64748b" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {showDocDropdown && docSearch && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.1)', zIndex: 100, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                      {filteredDocs.length === 0 ? <div style={{ padding: 15, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>No documents found</div> : filteredDocs.map(d => (
                        <div key={d._id} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => { 
                          const exists = (formData.linkedDocuments || []).find(doc => doc.id === d._id);
                          if (!exists) {
                            setFormData({ 
                              ...formData, 
                              linkedDocuments: [...(formData.linkedDocuments || []), { id: d._id, number: d.number, docType: d.type, customerName: d.customerName }] 
                            });
                          }
                          setDocSearch(''); 
                          setShowDocDropdown(false); 
                        }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={styles.badge(d.type)}>{d.type === 'invoice' ? 'INV' : 'CO'}</span>
                            <span style={{ fontWeight: 700, fontSize: 13 }}>{d.number}</span>
                          </div>
                          <span style={{ fontSize: 12, color: '#64748b' }}>{d.customerName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, padding: '20px 30px', borderTop: '1px solid #f1f5f9' }}>
                <button type="button" style={{ ...styles.btn('outline'), flex: 1 }} onClick={() => setShowProjectModal(false)}>Cancel</button>
                <button type="submit" style={{ ...styles.btn('primary'), flex: 1 }} disabled={saving}>
                  {saving ? <Loader2 size={18} className="animate-spin" /> : editingItem ? 'Update Project' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div style={styles.overlay} onClick={() => setShowExpenseModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', background: '#0A5F38', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ p: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                  <Plus size={20} color="#fff" />
                </div>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{editingItem ? 'Edit Expense' : 'Add Expense Entries'}</h2>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: 0 }}>Log financial transactions</p>
                </div>
              </div>
              <button onClick={() => setShowExpenseModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                <X size={22} color="#fff" />
              </button>
            </div>
            {projects.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <FolderOpen size={48} style={{ color: '#cbd5e1', marginBottom: 16 }} />
                <p style={{ color: '#64748b', fontWeight: 600, marginBottom: 20 }}>No projects found. You need to create a project before adding expenses.</p>
                <button
                  style={styles.btn('primary')}
                  onClick={() => { setShowExpenseModal(false); openProjectModal(); }}
                >
                  <Plus size={14} /> Create New Project
                </button>
              </div>
            ) : (
              <form onSubmit={handleSaveExpense}>
                <div className="custom-scrollbar" style={styles.modalBody}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 24 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Project *</label>
                      <FilterDropdown
                        value={formData.projectId || ''}
                        onChange={e => setFormData({ ...formData, projectId: e.target.value })}
                        options={[
                          { value: '', label: 'Select Project' },
                          ...projects.map(p => ({ value: p._id, label: p.projectName }))
                        ]}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Employee *</label>
                      <FilterDropdown
                        value={formData.employeeName || ''}
                        onChange={e => {
                          const emp = employees.find(emp => emp.name === e.target.value);
                          setFormData({ ...formData, employeeName: e.target.value, employeeId: emp?.id || emp?._id || null, employeeRole: emp?.role || 'employee' });
                        }}
                        options={[
                          { value: '', label: 'Select Employee' },
                          ...employees.map(e => ({ value: e.name, label: `${e.name} ${e.role === 'admin' ? '(Admin)' : ''}` }))
                        ]}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Date *</label>
                      <input type="date" style={{ width: '100%', padding: '11px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14 }} value={formData.date || ''} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                    </div>
                  </div>

                  {editingItem ? (
                    // Single Edit View
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 150px', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Amount *</label>
                        <input style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #cbd5e1' }} type="number" step="0.01" value={formData.amount || ''} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Description *</label>
                        <input style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #cbd5e1' }} value={formData.expenseDescription || ''} onChange={e => setFormData({ ...formData, expenseDescription: e.target.value })} placeholder="Purpose of expense..." />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Notes</label>
                        <input style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #cbd5e1' }} value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Add notes..." />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Link Doc</label>
                        <select
                          style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                          value={formData.linkedDocumentNumber || ''}
                          onChange={e => {
                            const doc = (projects.find(p => p._id === formData.projectId)?.linkedDocuments || []).find(d => d.number === e.target.value);
                            setFormData({ ...formData, linkedDocumentNumber: e.target.value, linkedDocumentType: doc?.docType || null });
                          }}
                        >
                          <option value="">None</option>
                          {(projects.find(p => p._id === formData.projectId)?.linkedDocuments || []).map((doc, i) => (
                            <option key={i} value={doc.number}>{doc.number} ({doc.customerName || 'N/A'})</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    // Batch Add View
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 150px 40px', gap: 12, marginBottom: 8, padding: '0 4px' }}>
                        <label style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount *</label>
                        <label style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description *</label>
                        <label style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
                        <label style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Link Doc</label>
                        <span></span>
                      </div>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '4px' }}>
                        {(formData.items || []).map((item, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 150px 40px', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                            <input
                              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                              type="number" step="0.01" value={item.amount}
                              onChange={e => {
                                const newItems = [...formData.items];
                                newItems[idx].amount = e.target.value;
                                setFormData({ ...formData, items: newItems });
                              }}
                              placeholder="0.00"
                            />
                            <input
                              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                              value={item.expenseDescription}
                              onChange={e => {
                                const newItems = [...formData.items];
                                newItems[idx].expenseDescription = e.target.value;
                                setFormData({ ...formData, items: newItems });
                              }}
                              placeholder="Description..."
                            />
                            <input
                              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                              value={item.notes}
                              onChange={e => {
                                const newItems = [...formData.items];
                                newItems[idx].notes = e.target.value;
                                setFormData({ ...formData, items: newItems });
                              }}
                              placeholder="Optional notes"
                            />
                            <select
                              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                              value={item.linkedDocumentNumber || ''}
                              onChange={e => {
                                const newItems = [...formData.items];
                                const doc = (projects.find(p => p._id === formData.projectId)?.linkedDocuments || []).find(d => d.number === e.target.value);
                                newItems[idx].linkedDocumentNumber = e.target.value;
                                newItems[idx].linkedDocumentType = doc?.docType || null;
                                setFormData({ ...formData, items: newItems });
                              }}
                            >
                              <option value="">None</option>
                              {(projects.find(p => p._id === formData.projectId)?.linkedDocuments || []).map((doc, i) => (
                                <option key={i} value={doc.number}>{doc.number}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => {
                                const newItems = formData.items.filter((_, i) => i !== idx);
                                setFormData({ ...formData, items: newItems });
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', justifyContent: 'center' }}
                              title="Remove row"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, items: [...(formData.items || []), { amount: '', expenseDescription: '', notes: '', linkedDocumentNumber: '', linkedDocumentType: '' }] })}
                        style={{ ...styles.btn('outline'), width: '100%', borderStyle: 'dashed', marginTop: 8, justifyContent: 'center' }}
                      >
                        <Plus size={16} /> Add Another Row
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 12, padding: '20px 30px', borderTop: '1px solid #f1f5f9' }}>
                  <button type="button" style={{ ...styles.btn('outline'), flex: 1 }} onClick={() => setShowExpenseModal(false)}>Cancel</button>
                  <button type="submit" style={{ ...styles.btn('primary'), flex: 1 }} disabled={saving}>
                    {saving ? 'Saving...' : editingItem ? 'Update Entry' : `Submit ${ (formData.items || []).filter(it => it.amount && it.expenseDescription).length || 0} Entries`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={styles.overlay} onClick={() => setShowConfirmModal(false)}>
          <div style={{ ...styles.modal, width: 400, textAlign: 'center', padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px 24px' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: confirmConfig.danger ? '#fee2e2' : '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                {confirmConfig.danger ? <Trash2 color="#ef4444" size={32} /> : <CheckCircle color="#16a34a" size={32} />}
              </div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 8, margin: 0 }}>{confirmConfig.title}</h3>
              <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, marginTop: 8 }}>{confirmConfig.message}</p>
            </div>
            <div style={{ padding: '0 24px 32px', display: 'flex', gap: 12 }}>
              <button style={{ ...styles.btn('outline'), flex: 1, justifyContent: 'center' }} onClick={() => setShowConfirmModal(false)}>No, Cancel</button>
              <button 
                style={{ ...styles.btn(confirmConfig.danger ? 'danger' : 'primary'), flex: 1, justifyContent: 'center' }} 
                onClick={() => {
                  confirmConfig.onConfirm();
                  setShowConfirmModal(false);
                }}
              >
                Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExpenses;
